import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Mail } from "lucide-react";

const EFFECTIVE_DATE = "December 2025";
const CONTACT_EMAIL = "stan@co-ownisrael.com"; // primary contact mailbox

const SECTIONS = [
  {
    id: "who-we-are",
    title: "Who we are",
    body: (
      <>
        <p>
          Co‑Own Israel (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) is a private initiative connecting Jewish families abroad with curated
          fractional ownership and investment opportunities for premium apartments in Israel. This Privacy Notice
          explains how we handle personal information you choose to share with us through this website.
        </p>
      </>
    ),
  },
  {
    id: "what-we-collect",
    title: "What we collect",
    body: (
      <>
        <p>We only collect information you actively provide via our forms. This may include:</p>
        <ul>
          <li>Your name, email address and phone number (including country code).</li>
          <li>Country of residence and preferred location(s) in Israel.</li>
          <li>Your stated ownership preference, indicative budget range, intended months of use per year, and your
            timeline for moving forward.</li>
          <li>Your path of interest (Co‑Own, Co‑Invest, or open to both).</li>
          <li>Your preferred time for a face‑to‑face video call, and any free‑form notes you choose to add.</li>
          <li>If you refer another family: their name, and any optional contact details, relationship and notes
            you choose to share.</li>
        </ul>
        <p>
          We do not knowingly collect sensitive categories of personal information (e.g. health, biometric, political
          opinions). We do not use third‑party advertising trackers, fingerprinting, or behavioural profiling on this site.
        </p>
      </>
    ),
  },
  {
    id: "how-we-use-it",
    title: "How we use your information",
    body: (
      <>
        <p>We use the information you share solely to:</p>
        <ul>
          <li>Contact you personally to arrange a private, confidential conversation about Co‑Own Israel.</li>
          <li>Understand what you are looking for so we can match you with suitable co‑owners, co‑investors and
            properties — and introduce you to trusted professionals (lawyer, mortgage broker, currency agent,
            property management company).</li>
          <li>Send you transactional emails such as the automatic confirmation that we have received your interest.</li>
          <li>If you refer another family: contact them with care and only if it appears to be a genuine fit.
            We will not disclose your identity to a referred family unless you explicitly ask us to.</li>
        </ul>
        <p>
          We do not send marketing emails, do not sell or rent your information to third parties, and do not share
          your details outside of the trusted professionals required to progress a specific opportunity — and only
          with your prior consent.
        </p>
      </>
    ),
  },
  {
    id: "lawful-basis",
    title: "Our lawful basis (UK / EU residents)",
    body: (
      <>
        <p>
          If you are located in the United Kingdom or the European Economic Area, we rely on the following lawful
          bases under the UK GDPR / EU GDPR:
        </p>
        <ul>
          <li><strong>Consent</strong> — when you voluntarily submit a form on our website.</li>
          <li><strong>Legitimate interests</strong> — to respond to your enquiry, organise a conversation,
            and follow up on referrals you initiate, in a manner you would reasonably expect.</li>
        </ul>
        <p>You may withdraw your consent at any time by emailing us (see <em>Contact</em> below).</p>
      </>
    ),
  },
  {
    id: "third-parties",
    title: "Service providers we use",
    body: (
      <>
        <p>To operate this website we rely on a small number of carefully selected service providers:</p>
        <ul>
          <li><strong>Database hosting</strong> — your submission is stored in a managed MongoDB database that
            powers this site.</li>
          <li><strong>Resend</strong> (transactional email) — used to deliver the confirmation email to you and
            the internal notification to our team.</li>
        </ul>
        <p>
          These providers process your data on our instructions, under contractual confidentiality obligations,
          and only for the purposes described above.
        </p>
      </>
    ),
  },
  {
    id: "retention",
    title: "How long we keep your information",
    body: (
      <>
        <p>
          We keep your information for as long as is reasonably necessary to evaluate suitability, follow up,
          and (where appropriate) introduce you to relevant professionals. If we do not proceed together, we will
          retain your data for up to <strong>24 months</strong> from your last contact with us, after which it
          will be deleted or anonymised, unless we are required to retain it longer by law.
        </p>
        <p>You can ask us to delete your data sooner at any time (see <em>Your rights</em> below).</p>
      </>
    ),
  },
  {
    id: "your-rights",
    title: "Your rights",
    body: (
      <>
        <p>
          Depending on where you live, you have rights over your personal information. These typically include:
        </p>
        <ul>
          <li>Access — request a copy of what we hold about you.</li>
          <li>Correction — ask us to fix anything inaccurate.</li>
          <li>Deletion — ask us to delete your information (subject to legal exceptions).</li>
          <li>Restriction or objection — ask us to stop or limit how we use your information.</li>
          <li>Portability — request your data in a portable format.</li>
          <li>Withdraw consent at any time.</li>
        </ul>
        <p>
          To exercise any of these rights, email us at the address below. We will respond within a reasonable
          timeframe and, where required, within statutory deadlines (e.g. 30 days under UK / EU GDPR).
        </p>
      </>
    ),
  },
  {
    id: "transfers",
    title: "International transfers",
    body: (
      <>
        <p>
          Because we serve families in Australia, the United Kingdom, France, Canada, the United States and Israel,
          your information may be processed in countries other than your country of residence — including by our
          email and database providers. Where required, we rely on appropriate safeguards (such as Standard
          Contractual Clauses) to protect your information during such transfers.
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    title: "Cookies & tracking",
    body: (
      <>
        <p>
          This website does not currently set advertising or analytics cookies. Strictly necessary cookies may
          be set by our hosting platform to keep the site functioning. If we add analytics in the future we will
          update this notice and, where required, request your consent.
        </p>
      </>
    ),
  },
  {
    id: "children",
    title: "Children",
    body: (
      <>
        <p>
          This website is intended for adults. We do not knowingly collect personal information from individuals
          under 18 years of age.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "Changes to this notice",
    body: (
      <>
        <p>
          We may update this Privacy Notice from time to time. The effective date at the top of this page will
          always reflect the latest version. Material changes will be communicated by updating this page.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "Contact us",
    body: (
      <>
        <p>
          For any privacy question, request, or to exercise your rights, please email{" "}
          <a className="underline decoration-[var(--gold)]/60 underline-offset-4 hover:text-[var(--gold)]" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
          . We treat every request with discretion.
        </p>
      </>
    ),
  },
];

export default function Privacy() {
  const navigate = useNavigate();
  useEffect(() => {
    document.title = "Privacy Notice — Co-Own Israel";
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <main data-testid="privacy-page" className="bg-[var(--bone)] text-[var(--ink)] min-h-screen">
      {/* Top bar */}
      <div className="border-b border-[var(--border-soft)]">
        <div className="container mx-auto px-6 md:px-12 lg:px-16 max-w-[1100px] flex items-center justify-between py-5">
          <Link
            to="/"
            data-testid="privacy-brand"
            className="font-serif-display text-lg md:text-xl leading-none tracking-tight flex items-baseline gap-2"
          >
            <span className="text-[var(--ink)]">Co-Own</span>
            <span className="font-bold not-italic" style={{ color: "#E87A4C" }}>Israel</span>
          </Link>
          <Button
            data-testid="privacy-back-btn"
            variant="ghost"
            onClick={() => navigate("/")}
            className="rounded-full border border-[var(--border-soft)] text-sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to site
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-6 md:px-12 lg:px-16 max-w-[1100px] py-16 md:py-24 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Sidebar — table of contents */}
          <aside className="lg:col-span-4 lg:sticky lg:top-24 self-start">
            <div className="text-overline text-[var(--ink)]/55 mb-3">Privacy notice</div>
            <h1 className="font-serif-display text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.05] tracking-tight">
              How we look after <span className="italic text-[var(--gold)]">your information.</span>
            </h1>
            <p className="mt-5 text-sm text-[var(--ink)]/60 font-light">
              Effective {EFFECTIVE_DATE}
            </p>

            <nav className="hidden lg:block mt-10 border-l border-[var(--border-soft)] pl-5 space-y-2.5">
              {SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  data-testid={`privacy-toc-${s.id}`}
                  className="block text-sm text-[var(--ink)]/65 hover:text-[var(--ink)] transition-colors"
                >
                  {s.title}
                </a>
              ))}
            </nav>

            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="privacy-contact-link"
              className="mt-10 hidden lg:inline-flex items-center gap-2 text-sm border-b border-[var(--gold)]/70 pb-0.5 hover:text-[var(--gold)] transition-colors"
            >
              <Mail className="h-4 w-4" />
              Contact our privacy desk
            </a>
          </aside>

          {/* Body */}
          <article className="lg:col-span-8 space-y-12 md:space-y-14">
            <p className="text-base md:text-lg text-[var(--ink)]/75 leading-relaxed font-light">
              This page explains, plainly and honestly, what information we collect when you register your interest
              or refer another family on co‑ownisrael.com, what we do with it, who we share it with, and the rights
              you have over it. If anything here is unclear, please email us at{" "}
              <a className="underline decoration-[var(--gold)]/60 underline-offset-4 hover:text-[var(--gold)]" href={`mailto:${CONTACT_EMAIL}`}>
                {CONTACT_EMAIL}
              </a>
              .
            </p>

            {SECTIONS.map((s) => (
              <section
                key={s.id}
                id={s.id}
                data-testid={`privacy-section-${s.id}`}
                className="scroll-mt-24"
              >
                <h2 className="font-serif-display text-2xl md:text-3xl tracking-tight leading-tight">
                  {s.title}
                </h2>
                <div className="mt-4 text-[var(--ink)]/75 text-base leading-relaxed font-light space-y-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_strong]:text-[var(--ink)] [&_strong]:font-medium [&_em]:text-[var(--ink)]/85">
                  {s.body}
                </div>
              </section>
            ))}

            <div className="pt-8 mt-2 border-t border-[var(--border-soft)]">
              <Button
                data-testid="privacy-back-bottom-btn"
                onClick={() => navigate("/")}
                className="rounded-full h-12 px-7 bg-[var(--ink)] text-[var(--bone)] hover:bg-[var(--ink-hover)]"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Co-Own Israel
              </Button>
            </div>
          </article>
        </div>
      </div>
    </main>
  );
}
